import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { Row, Col, Button, Card, CardBody, Spinner, Label } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import CommonForm from "Components/Common/CommonForm";
import styles from "../../MainPrintListTabs/css/printlabel.module.scss";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { useStudyTimepoints } from "hooks/useStudyTimepoints";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import SimpleBar from "simplebar-react";
import { templateApiService } from "modules/Library/Template/api/template.api";
import DOMPurify from "dompurify";
import Barcode from "react-barcode";
import { createRoot } from "react-dom/client";
import { showToastMessage } from "common/service/CommonService";
import { useQueryClient } from "@tanstack/react-query";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { VacutainerLabelFormValues } from "../helpers/Vaccutainerlabel.types";
import { vacutainerLabelColumns, vacutainerLabelFields } from "../helpers/Vaccutainer.constant";
import { useVacutainerLabelService } from "../api/useVacutainerLabelService";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import CommonButton from "Components/Common/CommonButton";
import {
  customSelectComponents,
  getCustomSelectStyles,
  handleAllMultiSelectChange,
} from "../../MainPrintListTabs/helper/reportSelectUtils";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const replacePlaceholders = (template: string, data: Record<string, any>) => {
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    return data[key.trim()] ?? "";
  });
};

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */

const VacutainerLabelList: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [individualCounts, setIndividualCounts] = useState<{ [key: number]: number }>({});
  const previewRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<VacutainerLabelFormValues>({
    defaultValues: {
      studyid: null,
      period: null,
      timepoint: ["ALL"],
    },
  });

  const selectedStudyId = watch("studyid");
  const selectedPeriod = watch("period");
  const selectedTimepoint = watch("timepoint");

  const { selectedStudy } = useGlobalStudy();
  const { hasPermission } = usePermission();

  const { dynamicPeriods, dynamicPhases } = useStudyTimepoints(
    Number(selectedStudyId) || 0,
  );

  const periodOptions = useMemo(() => {
    return dynamicPeriods.map((p: any) => ({
      label: String(p.periodorder).padStart(2, "0"),
      value: String(p.periodorder).padStart(2, "0"),
    }));
  }, [dynamicPeriods]);

  useEffect(() => {
    if (selectedStudy) {
      setValue("studyid", Number(selectedStudy.studyid));
    }
  }, [selectedStudy, setValue]);

  // Reset period and timepoint when studyid changes
  useEffect(() => {
    setValue("period", periodOptions[0]?.value || null);
    setValue("timepoint", ["ALL"]);
  }, [selectedStudyId, setValue, periodOptions]);

  const timepointOptions = useMemo(() => {
    const opts = dynamicPhases.map((p: any) => ({
      label: p.label,
      value: p.label,
    }));
    return [{ label: "All Timepoints", value: "ALL" }, ...opts];
  }, [dynamicPhases]);

  useEffect(() => {
    if (!selectedPeriod && periodOptions.length > 0) {
      setValue("period", periodOptions[0].value);
    }
  }, [selectedPeriod, periodOptions, setValue]);

  useEffect(() => {
    if (!selectedTimepoint || (Array.isArray(selectedTimepoint) && selectedTimepoint.length === 0)) {
      setValue("timepoint", ["ALL"]);
    }
  }, [selectedTimepoint, setValue]);

  const { DropDownAllQuery: studyDropdownQuery } = useMasterStudyService({
    enabledDropDown: true
  });

  const { SelectAllQuery: vacutainerSelectAllQuery, UpdateLabelPrintMutation } =
    useVacutainerLabelService({
      studyid: selectedStudyId ? Number(selectedStudyId) : null,
      period: selectedPeriod,
      timepoint: selectedTimepoint,
      searchtext: searchText,
    });

  const studyOptions = useMemo(
    () =>
      (studyDropdownQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || [],
    [studyDropdownQuery.data]
  );

  const selectedStudyLabel = useMemo(
    () => studyOptions.find((opt: any) => opt.value === selectedStudyId)?.label || "",
    [studyOptions, selectedStudyId]
  );

  const apiResponse = vacutainerSelectAllQuery.data as any;
  const rawTableData: any[] = useMemo(
    () => (apiResponse?.data || []).filter((v: any) => !v.printid),
    [apiResponse?.data]
  );

  const tableData = useMemo(() => {
    return rawTableData.filter((row: any) => {
      // Period filter
      if (selectedPeriod && selectedPeriod !== "ALL") {
        const rowPeriod = String(row.period_number || "").padStart(2, "0");
        const targetPeriod = String(selectedPeriod).padStart(2, "0");
        if (rowPeriod !== targetPeriod && String(row.period_number) !== String(selectedPeriod)) {
          return false;
        }
      }

      // Timepoint filter
      if (selectedTimepoint) {
        if (Array.isArray(selectedTimepoint)) {
          if (!selectedTimepoint.includes("ALL") && selectedTimepoint.length > 0) {
            const rowTp = String(row.timepoint_number || "").trim();
            const rowTpNum = Number(rowTp);
            const match = selectedTimepoint.some((tp: any) => {
              const tpStr = String(tp).trim();
              if (tpStr === rowTp) return true;
              if (!isNaN(Number(tpStr)) && !isNaN(rowTpNum) && Number(tpStr) === rowTpNum) return true;
              return false;
            });
            if (!match) return false;
          }
        } else if (selectedTimepoint !== "ALL") {
          const rowTp = String(row.timepoint_number || "").trim();
          const targetTp = String(selectedTimepoint).trim();
          if (rowTp !== targetTp && Number(rowTp) !== Number(targetTp)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [rawTableData, selectedPeriod, selectedTimepoint]);

  const totalRecords = tableData.length;

  const isFilterValid = Boolean(
    selectedStudyId &&
      selectedPeriod &&
      selectedTimepoint &&
      (Array.isArray(selectedTimepoint) ? selectedTimepoint.length > 0 : !!selectedTimepoint)
  );

  useEffect(() => {
    setSelectedRows([]);
    setCurrentIndex(0);
  }, [selectedStudyId, selectedPeriod, selectedTimepoint, searchText]);

  useEffect(() => {
    if (selectedStudyId) {
      fetchTemplate();
    }
  }, [selectedStudyId]);

  const fetchTemplate = async () => {
    try {
      setIsTemplateLoading(true);
      const res = await templateApiService.getSelectAllData({
        searchtext: "Vacutainer Label",
        pagenumber: 1,
        pagesize: 10,
        status: "A",
      });
      const templates = res?.data || [];
      const labelTemplate = templates.find((t: any) => {
        const name = String(t.templatename || t.name || "").toLowerCase();
        return name.includes("vacutainer") || name.includes("sample label");
      });

      if (labelTemplate) {
        setTemplateHtml(labelTemplate.designtemplate);
      } else if (templates.length > 0) {
        setTemplateHtml(templates[0].designtemplate);
      }
    } catch (error) {
      console.error("Error fetching template", error);
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const selectedAssignmentIds = useMemo(
    () => selectedRows.map((row) => row.samplecollectionid),
    [selectedRows],
  );

  const isAllSelected = useMemo(() => {
    return (
      tableData.length > 0 &&
      tableData.every((row: any) =>
        selectedAssignmentIds.includes(row.samplecollectionid),
      )
    );
  }, [tableData, selectedAssignmentIds]);

  const handleSelectAll = (isChecked: boolean) => {
    setSelectedRows(isChecked ? tableData : []);
    setCurrentIndex(0);
  };

  const toggleSelection = (row: any, isChecked: boolean) => {
    setSelectedRows((prev) =>
      isChecked
        ? [...prev, row]
        : prev.filter((r) => r.samplecollectionid !== row.samplecollectionid),
    );
    setCurrentIndex(0);
  };

  const getCountForId = (id: number) => individualCounts[id] || 1;

  const mapSubjectToData = (row: any) => {
    const barcodeValue = row.vialbarcode
      ? row.vialbarcode
      : `${row.subjectno || "SUB"}-P${row.period_number || "01"}-000`;

    const barcodeHtml = `<div class="barcode-target" data-barcode="${barcodeValue}"></div>`;

    return {
      SubjectNo: row.subjectno || "N/A",
      StudyCode: selectedStudyLabel || "N/A",
      Period: row.period_number || "N/A",
      Timepoint: row.timepoint_number || "N/A",
      VialBarcode: row.vialbarcode || "N/A",
      barcodeno: barcodeValue,
      Barcode: "___BARCODE_MARKER___",
      BarcodeHtml: barcodeHtml,
    };
  };

  const previewList = selectedRows.length > 0 ? selectedRows : [];
  const currentSubject = previewList[currentIndex] || null;

  const mappedPreviewHtml = useMemo(() => {
    if (!templateHtml || !currentSubject) return null;

    const mappedData = mapSubjectToData(currentSubject);

    let renderedHtml = replacePlaceholders(templateHtml, mappedData);
    renderedHtml = DOMPurify.sanitize(renderedHtml, {
      ADD_TAGS: ["style"],
      WHOLE_DOCUMENT: true,
      FORCE_BODY: true,
    });

    return renderedHtml.replace("___BARCODE_MARKER___", mappedData.BarcodeHtml);
  }, [templateHtml, currentSubject, selectedStudyLabel]);

  // Barcode Rendering Effect - useLayoutEffect for zero-blink rendering
  useLayoutEffect(() => {
    if (previewRef.current && currentSubject) {
      const targets = previewRef.current?.querySelectorAll(".barcode-target");
      targets?.forEach((target) => {
        const val = target.getAttribute("data-barcode") || "";

        if (target.children.length === 0) {
          const root = createRoot(target as HTMLElement);
          root.render(
            <Barcode
              value={val}
              format="CODE128"
              width={1.5}
              height={55}
              displayValue={false}
              margin={0}
            />,
          );
        }
      });
    }
  }, [
    mappedPreviewHtml,
    selectedRows,
    individualCounts,
    vacutainerSelectAllQuery.isFetching,
    UpdateLabelPrintMutation.isPending,
  ]);

  const handlePrint = () => {
    if (selectedRows.length === 0) return;

    const payload = selectedRows
      .map((row) => {
        const id = row.samplecollectionid;
        if (id === undefined || id === null) return null;
        return { id, quantity: getCountForId(id) };
      })
      .filter(Boolean);

    UpdateLabelPrintMutation.mutate(
      {
        samplecollection: payload,
        studyid: selectedStudyId ? Number(selectedStudyId) : null,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Vacutainer Label record updated successfully!",
          );
          queryClient.invalidateQueries({
            queryKey: ["vacutainer-label-selectAll"],
          });
          setSelectedRows([]);
          setIndividualCounts({});
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to update print history",
          );
        },
      },
    );
  };

  return (
    <Row>
      <Col lg={7}>
        <div className="mb-4">
          <Row className="gy-3 form_input align-items-start">
            <Col md={4}>
              <CommonForm
                fields={vacutainerLabelFields(
                  studyOptions,
                  periodOptions,
                  timepointOptions,
                  selectedStudyId
                ).slice(0, 1)}
                control={control}
                errors={errors}
                columns={1}
              />
            </Col>
            <Col md={4} className="mb-2 position-relative">
              <Label className="mb-2">
                Period <span className="text-danger">*</span>
              </Label>
              <Controller
                name="period"
                control={control}
                render={({ field: controllerField }) => {
                  const isAll = controllerField.value === "ALL";
                  const selectedOpt = periodOptions.find((opt: any) => opt.value === controllerField.value) || null;

                  return (
                    <Select
                      options={periodOptions}
                      value={selectedOpt}
                      isDisabled={!selectedStudyId}
                      onChange={(opt: any) => {
                        setValue("period", opt ? opt.value : "ALL", { shouldValidate: true, shouldDirty: true });
                      }}
                      placeholder="Select Period"
                      menuPortalTarget={document.body}
                      className="common-select"
                      classNamePrefix="common-select"
                      styles={getCustomSelectStyles(!!errors?.period, isAll)}
                    />
                  );
                }}
              />
            </Col>
            <Col md={4} className="mb-2 position-relative">
              <Label className="mb-2">
                Timepoint <span className="text-danger">*</span>
              </Label>
              <Controller
                name="timepoint"
                control={control}
                render={({ field: controllerField }) => {
                  const currentVals = Array.isArray(controllerField.value)
                    ? controllerField.value
                    : controllerField.value
                      ? [controllerField.value]
                      : ["ALL"];
                  const selectedOpts = timepointOptions.filter((opt) =>
                    currentVals.includes(opt.value)
                  );
                  const isAll = currentVals.includes("ALL");

                  return (
                    <Select
                      isMulti
                      options={timepointOptions}
                      value={selectedOpts}
                      isDisabled={!selectedStudyId}
                      onChange={(selected: any) =>
                        handleAllMultiSelectChange("timepoint", selected, currentVals, setValue)
                      }
                      placeholder="Select Timepoint"
                      menuPortalTarget={document.body}
                      className="common-select"
                      classNamePrefix="common-select"
                      components={customSelectComponents}
                      styles={getCustomSelectStyles(!!errors?.timepoint, isAll)}
                    />
                  );
                }}
              />
            </Col>
          </Row>
        </div>

        {isFilterValid && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6
                className="fw-700 mb-0 small text-uppercase ls-1"
                style={{ color: "#008080" }}
              >
                Vacutainer Details
              </h6>
              <div
                className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                style={{ width: "250px", marginBottom: 0 }}
              >
                <i className={`ri-search-line ${sf.searchIcon}`} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchText}
                  className={sf.searchInput}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                {searchText && (
                  <i
                    className={`ri-close-line ${sf.clearIcon}`}
                    onClick={() => setSearchText("")}
                  />
                )}
              </div>
            </div>
              <CommonTableIndexWise
                columns={vacutainerLabelColumns(
                  selectedAssignmentIds,
                  toggleSelection,
                  isAllSelected,
                  handleSelectAll
                )}
                data={tableData}
                className={tableStyles.table}
                hideAction={true}
                maxHeight="400px"
                stickyHeader={true}
                loading={
                  vacutainerSelectAllQuery.isLoading ||
                  vacutainerSelectAllQuery.isFetching
                }
                totalRecords={totalRecords}
                paginationShow={false}
              />
          </div>
        )}
      </Col>

      {isFilterValid && (
        <Col lg={5}>
          <Card className="border shadow-none rounded-4 overflow-hidden">
            <CardBody className="p-3 d-flex flex-column">
              <div className="mb-3">
                <h6 className="fw-700 mb-1">Label Preview</h6>
                <p className="text-muted extra-small mb-0">
                  Vacutainer Label Preview
                </p>
              </div>

              <div className={styles.preview_container} ref={previewRef}>
                {isTemplateLoading ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : currentSubject ? (
                  <div
                    className={styles.simulated_label_wrapper}
                    dangerouslySetInnerHTML={{
                      __html: mappedPreviewHtml || "",
                    }}
                  />
                ) : (
                  <div className="text-center text-muted p-4 border rounded-3 bg-white w-100">
                    <i className="ri-flask-line fs-1 d-block mb-2 opacity-25" />
                    <p className="small mb-0">
                      Select a subject from the list to preview their label
                    </p>
                  </div>
                )}
              </div>

              {previewList.length > 1 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(Math.max(0, currentIndex - 1))
                      }
                      disabled={currentIndex === 0}
                    >
                      <i className="ri-arrow-left-s-line fs-5" />
                    </Button>

                    <div className="px-3 py-1 bg-light rounded-3">
                      <span className="fw-700 small text-primary">
                        {currentIndex + 1} of {previewList.length}
                      </span>
                    </div>

                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(
                          Math.min(previewList.length - 1, currentIndex + 1),
                        )
                      }
                      disabled={currentIndex === previewList.length - 1}
                    >
                      <i className="ri-arrow-right-s-line fs-5" />
                    </Button>
                  </div>
                </div>
              )}
              {hasPermission(PERMISSION_MODULES.VACUTAINER_LABEL, PERMISSION_ACTIONS.PRINT) && (
                <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                  <span className="text-muted small fw-semibold">Quantity</span>
                  <div className="d-flex align-items-center gap-3">
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.samplecollectionid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: Math.max(1, getCountForId(id) - 1),
                        }));
                      }}
                      disabled={
                        !currentSubject ||
                        (currentSubject &&
                          getCountForId(currentSubject.samplecollectionid) <= 1)
                      }
                    >
                      <i className="ri-subtract-line fs-6" />
                    </Button>
                    <span
                      className="fw-bold text-primary"
                      style={{ minWidth: "20px", textAlign: "center" }}
                    >
                      {currentSubject
                        ? getCountForId(currentSubject.samplecollectionid)
                        : 0}
                    </span>
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.samplecollectionid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: getCountForId(id) + 1,
                        }));
                      }}
                      disabled={!currentSubject}
                    >
                      <i className="ri-add-line fs-6" />
                    </Button>
                  </div>
                </div>
              )}

              <CommonButton
                module={PERMISSION_MODULES.VACUTAINER_LABEL}
                action={PERMISSION_ACTIONS.PRINT}
                color="primary"
                block
                className="py-2 rounded-3 d-flex align-items-center justify-content-center gap-2 fw-700 shadow-sm mt-auto"
                disabled={previewList.length === 0 || UpdateLabelPrintMutation.isPending}
                onClick={handlePrint}
              >
                {UpdateLabelPrintMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-printer-line" />
                )}{" "}
                Print Labels
              </CommonButton>
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default VacutainerLabelList;
